import {
  getMostSpecificActiveHighlightId,
  Thread,
} from 'components/DSYS/editor/utils/highlightHelpers';
import {useEffect, useState} from 'react';

const getThreadIdFromEventTarget = (
  target: EventTarget | null,
  containerEl: HTMLElement,
  threads: readonly Thread[]
): string | null => {
  if (!(target instanceof Element)) return null;
  const highlightEl = target.closest<HTMLElement>('[data-highlight-id]');
  if (!highlightEl || !containerEl.contains(highlightEl)) return null;

  // Use the same logic as the editor to select the most specific overlapping comment
  return getMostSpecificActiveHighlightId(highlightEl, threads);
};

export const useCommentsPanelHoveredThread = ({
  containerEl,
  threads,
}: {
  containerEl: HTMLElement | null;
  threads: readonly Thread[];
}) => {
  const [hoveredThreadId, setHoveredThreadId] = useState<string | null>(null);

  useEffect(() => {
    if (!containerEl || !threads.length) {
      setHoveredThreadId(null);
      return;
    }

    const handleMouseOver = (event: MouseEvent) => {
      const threadId = getThreadIdFromEventTarget(event.target, containerEl, threads);
      if (threadId) {
        setHoveredThreadId(threadId);
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      const nextThreadId = getThreadIdFromEventTarget(event.relatedTarget, containerEl, threads);
      setHoveredThreadId(nextThreadId);
    };

    const handleFocusIn = (event: FocusEvent) => {
      const threadId = getThreadIdFromEventTarget(event.target, containerEl, threads);
      if (threadId) {
        setHoveredThreadId(threadId);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const nextThreadId = getThreadIdFromEventTarget(event.relatedTarget, containerEl, threads);
      setHoveredThreadId(nextThreadId);
    };

    containerEl.addEventListener('mouseover', handleMouseOver, true);
    containerEl.addEventListener('mouseout', handleMouseOut, true);
    containerEl.addEventListener('focusin', handleFocusIn, true);
    containerEl.addEventListener('focusout', handleFocusOut, true);

    return () => {
      containerEl.removeEventListener('mouseover', handleMouseOver, true);
      containerEl.removeEventListener('mouseout', handleMouseOut, true);
      containerEl.removeEventListener('focusin', handleFocusIn, true);
      containerEl.removeEventListener('focusout', handleFocusOut, true);
    };
  }, [containerEl, threads]);

  return hoveredThreadId;
};
